import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ModalRendererComponent } from '../../widgets/modal-renderer/modal-renderer.component';
import { getAvatarBgColor } from '../../../helpers/avatar.helper';
import { InitialsService } from '../../../helpers/initial.service';
import { FORM_ID } from '../../../helpers/form-id.enum';
import { AuthService } from '../../../api/services/auth.service';
import { Subject, takeUntil } from 'rxjs';
import { NoDataViewComponent } from '../no-data-view/no-data-view.component';
import { CommonService } from '../../../api/services/common.service';
import { GraphServiceService } from '../../../api/services/graph-service.service';
import {
  COLORS,
  PRIORITY_COLORS,
  RISK_COLORS,
  STATUS_COLORS,
} from '../../../helpers/constants/colors.constant';
import { ThemeColorService } from '../../../helpers/theme-color.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-task-table',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ModalRendererComponent,
    NoDataViewComponent,
  ],
  templateUrl: './task-table.component.html',
  styleUrl: './task-table.component.css',
})
export class TaskTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formData: any;
  @Input() isWriteAccess: boolean = true;
  @Input() viewMode: any;
  @Input() filterOptions: any;
  @Input() tasksLimit: any = 10;
  @Input() isLoading: any;
  @Input() taskAssigned: any;
  @Input() selectAllAcrossPages: any;
  @Input() hideAssignedTo: boolean = false;
  @Output() viewMoreClicked = new EventEmitter<void>();
  @Output() clearAll = new EventEmitter<{ formId: string; options?: any }>();
  @Output() loadData = new EventEmitter<{ formId: string; options?: any }>();
  @Output() selectionChanged = new EventEmitter<string[]>();
  @Output() closeDropdown = new EventEmitter<any>();
  @Input() resetSelectionTrigger: boolean = false;
  @ViewChild(ModalRendererComponent) modalRendererRef!: ModalRendererComponent;
  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLElement>;

  @Input() selectedIds: any[] = [];
  @Input() searchValue: string = '';
  @Input() showHeader: boolean = true;
  @Output() searchChange = new EventEmitter<string>();
  @Output() filterClick = new EventEmitter<void>();
  @Output() tagSelected = new EventEmitter<string>();

  localSearch: string = '';
  accessToken: any;
  modalTitle: any;
  activeModalContext: any;
  openMenuId: string | null = null;
  openAboveId: string | null = null;
  expandedIds = new Set<string>();
  currentSort: { [key: string]: 'asc' | 'desc' } = {};
  sortValue: string = '_id';
  profilePhotoUrls: { [email: string]: string } = {};
  tooltipMap: { [key: string]: boolean } = {};
  tooltipPosition: { top: number; left: number } | null = null;
  currentUserPhotoUrl: string | null = null;
  private currentUserEmail: string | null = null;
  private destroy$ = new Subject<void>();

  readonly skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  get colspanCount(): number {
    let count = 8; // base columns: Task Name, Weightage, Due Date, Progress, Status, Priority, Risk, Action
    if (this.taskAssigned === '' && this.isWriteAccess) {
      count++; // checkbox column
    }
    if (!this.hideAssignedTo) {
      count++; // assigned to column
    }
    return count;
  }

  constructor(
    private router: Router,
    private initialsService: InitialsService,
    private auth: AuthService,
    private commonService: CommonService,
    private graphService: GraphServiceService,
    public themeColorService: ThemeColorService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.accessToken = this.auth.token;
    this.currentUserEmail = localStorage.getItem('email');
    if (this.auth.latestProfilePhotoUrl) {
      this.currentUserPhotoUrl = this.auth.latestProfilePhotoUrl;
    }
    this.auth.profilePhotoUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((photoUrl?: any) => {
        if (photoUrl) {
          this.currentUserPhotoUrl = photoUrl;
        } else {
          this.currentUserPhotoUrl = null;
        }
      });
    this.getParams();
    window.addEventListener('scroll', this.handleScroll, true);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('scroll', this.handleScroll, true);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['taskAssigned'])
      this.taskAssigned = changes['taskAssigned'].currentValue;
    if (changes['tasksLimit'])
      this.tasksLimit = changes['tasksLimit'].currentValue;
    if (changes['searchValue'])
      this.localSearch = changes['searchValue'].currentValue ?? '';
  }

  getAssigneePhotoUrl(item: any): string {
    if (
      item.assignee?.email &&
      this.currentUserEmail &&
      item.assignee.email === this.currentUserEmail &&
      this.currentUserPhotoUrl
    ) {
      return this.currentUserPhotoUrl;
    }
    const photoKey = item.assignee?.profilePhoto;
    if (photoKey) {
      return `${photoKey}`;
    }
    return './assets/media/icons/default-user-icon.png';
  }

  handleImageError(event: any) {
    event.target.src = './assets/media/icons/default-user-icon.png';
  }

  onSearch(value: string): void {
    this.localSearch = value;
    this.searchChange.emit(value);
  }

  onFilterClick(): void {
    this.filterClick.emit();
  }

  onTagClick(tag: string, event: Event): void {
    event.stopPropagation();
    this.tagSelected.emit(tag);
  }

  clearSearch(input: HTMLInputElement): void {
    input.value = '';
    this.localSearch = '';
    this.searchChange.emit('');
  }

  getParams(): void {
    const isMyTaskContext = this.router.url.includes('/my-tasks');
    const storageKey = isMyTaskContext ? 'myTaskView' : 'tasksView';
    const s = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
    this.taskAssigned = s.taskAssigned ?? this.taskAssigned ?? '';
    this.tasksLimit = s.taskLimit ?? s.limit ?? s.tasksLimit ?? 10;
    if (s.sort) {
      this.sortValue = s.sort;
      const isDesc = s.sort.startsWith('-');
      const colKey = isDesc ? s.sort.substring(1) : s.sort;
      this.currentSort = { [colKey]: isDesc ? 'desc' : 'asc' };
    } else {
      this.sortValue = '_id';
      this.currentSort = {};
    }
  }

  // ── Expand / collapse description ────────────────────────────────
  toggleExpand(itemId: string, event: Event) {
    event.stopPropagation();
    if (this.expandedIds.has(itemId)) {
      this.expandedIds.delete(itemId);
    } else {
      this.expandedIds.add(itemId);
    }
  }

  isExpanded(itemId: string): boolean {
    return this.expandedIds.has(itemId);
  }

  buildQueryParams(state: any): any {
    const queryParams: any = {};
    if (state.taskSearch) queryParams.taskSearch = state.taskSearch;

    const taskStatus = state.taskStatus || state.status;
    if (taskStatus && taskStatus.length) {
      queryParams.status = Array.isArray(taskStatus)
        ? taskStatus.join(',')
        : taskStatus;
    }

    const taskPriority = state.taskPriority || state.priority;
    if (taskPriority && taskPriority.length) {
      queryParams.priority = Array.isArray(taskPriority)
        ? taskPriority.join(',')
        : taskPriority;
    }

    const riskStatus = state.riskStatus || state.risk;
    if (riskStatus && riskStatus.length) {
      queryParams.risk = Array.isArray(riskStatus)
        ? riskStatus.join(',')
        : riskStatus;
    }

    const domain = state.domainSubdomainMappings || state.domain;
    if (domain && domain.length) {
      queryParams.domain = Array.isArray(domain) ? domain.join(',') : domain;
    }

    const assignedUser = state.assignedUser || state.assigned;
    if (assignedUser && assignedUser.length) {
      queryParams.assigned = Array.isArray(assignedUser)
        ? assignedUser.join(',')
        : assignedUser;
    }

    const standardName =
      state.standardName || state['mappedStandards.standardName'];
    if (standardName && standardName.length) {
      queryParams.standardName = Array.isArray(standardName)
        ? standardName.join(',')
        : standardName;
    }

    if (state.tags && state.tags.length) {
      queryParams.tags = Array.isArray(state.tags)
        ? state.tags.join(',')
        : state.tags;
    }

    if (state.taskAssigned) {
      queryParams.taskAssigned = state.taskAssigned;
    }
    return queryParams;
  }

  // ── Navigation ───────────────────────────────────────────────────
  onRowClick(event: MouseEvent, item: any) {
    const selected = window.getSelection()?.toString();
    if (selected?.trim()) return;

    // Determine navigation source based on current URL
    const isMyTaskContext = this.router.url.includes('/my-tasks');
    const isFrameworkLibrary = this.router.url.includes('/framework-library') || this.router.url.includes('/framework-details');
    const storageKey = isMyTaskContext ? 'myTaskView' : 'tasksView';
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
    const taskAssigned = saved?.taskAssigned || '';

    // Persist source context so task-view back-button knows where to return
    sessionStorage.setItem(
      'taskViewNav',
      JSON.stringify({
        source: isFrameworkLibrary ? 'framework-library' : (isMyTaskContext ? 'my-tasks' : 'tasks'),
        taskAssigned: isMyTaskContext ? taskAssigned : '',
        backUrl: this.router.url,
      }),
    );

    const queryParams = this.buildQueryParams(saved);
    this.router.navigate(['/eciso/tasks', item.taskId], { queryParams });
  }

  navigateToCompliance(item: any, event: Event) {
    event.stopPropagation();
    const isMyTaskContext = this.router.url.includes('/my-tasks');
    const isFrameworkLibrary = this.router.url.includes('/framework-library') || this.router.url.includes('/framework-details');
    const storageKey = isMyTaskContext ? 'myTaskView' : 'tasksView';
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
    const taskAssigned = saved?.taskAssigned || '';

    sessionStorage.setItem(
      'taskViewNav',
      JSON.stringify({
        source: isFrameworkLibrary ? 'framework-library' : (isMyTaskContext ? 'my-tasks' : 'tasks'),
        taskAssigned: isMyTaskContext ? taskAssigned : '',
        backUrl: this.router.url,
      }),
    );

    const queryParams = this.buildQueryParams(saved);
    this.router.navigate(['/eciso/tasks', item.taskId], {
      fragment: 'compliance-section',
      queryParams,
    });
  }

  viewMore() {
    this.viewMoreClicked.emit();
  }

  // ── Domain icon ───────────────────────────────────────────────────
  getDomainInitials(taskId: string): string {
    if (!taskId) return '?';
    const parts = taskId.split('.');
    return parts[0]?.slice(0, 3).toUpperCase() ?? '??';
  }

  getDomainColor(taskId: string): string {
    if (!taskId) return '#6b7280';
    const prefix = taskId.split('.')[0]?.toLowerCase() ?? '';
    return COLORS[prefix] ?? '#6b7280';
  }

  getDomainBg(taskId: string): { [k: string]: string } {
    const color = this.getDomainColor(taskId);
    return { background: color + '22', color };
  }

  // ── Priority ─────────────────────────────────────────────────────
  getPriority(priority: string): string {
    return priority?.trim() || 'Do Later';
  }

  // ── Risk ─────────────────────────────────────────────────────────

  // ── Status ───────────────────────────────────────────────────────

  // ── Badge Customization ──────────────────────────────────────────
  getBadgeClass(type: string, value: string): string {
    if (!value && type === 'risk') {
      return 'badge-low dark:badge-low';
    }
    return this.themeColorService.getBadgeClass(value) || '';
  }

  private parseDueDate(
    date: string | Date,
  ): { year: number; month: number; day: number } | null {
    if (!date) return null;
    if (typeof date === 'string') {
      const trimmed = date.trim();
      const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        return {
          year: parseInt(isoMatch[1], 10),
          month: parseInt(isoMatch[2], 10) - 1,
          day: parseInt(isoMatch[3], 10),
        };
      }
      const dmyMatch = trimmed.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
      if (dmyMatch) {
        return {
          year: parseInt(dmyMatch[3], 10),
          month: parseInt(dmyMatch[2], 10) - 1,
          day: parseInt(dmyMatch[1], 10),
        };
      }
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth(),
      day: d.getUTCDate(),
    };
  }

  // ── Due date ─────────────────────────────────────────────────────
  formatDueDate(date: string): string {
    if (!date) return '';
    try {
      const parts = this.parseDueDate(date);
      if (!parts) return date;
      const utcDate = new Date(Date.UTC(parts.year, parts.month, parts.day));
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(utcDate);
    } catch {
      return date;
    }
  }

  getDelayStatus(dueDate: string | Date) {
    const parts = this.parseDueDate(dueDate);
    if (!parts) return { type: 'delay', days: 0 };

    const endOnly = new Date(parts.year, parts.month, parts.day);
    const today = new Date();

    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    const diff = endOnly.getTime() - todayOnly.getTime();
    const days = Math.abs(Math.round(diff / 86400000));

    if (diff > 0) return { type: 'remaining', days };
    if (diff === 0) return { type: 'today', days: 0 };
    return { type: 'delay', days };
  }

  getDueDateRelative(date: string): { text: string; colorClass: string } {
    if (!date) return { text: '', colorClass: '' };
    try {
      const { type, days } = this.getDelayStatus(date);
      if (type === 'delay') {
        return { text: `${days} day overdue`, colorClass: 'text-danger' };
      }
      if (type === 'today') {
        return { text: 'Today', colorClass: 'text-danger' };
      }
      const text = `In ${days} day${days === 1 ? '' : 's'}`;
      if (days <= 3) {
        return { text, colorClass: 'text-danger' };
      }
      if (days <= 10) {
        return { text, colorClass: 'text-warning' };
      }
      return { text, colorClass: 'text-success' };
    } catch {
      return { text: '', colorClass: '' };
    }
  }

  // ── Progress ────────────────────────────────────────────────────
  getProgress(item: any): number {
    if (typeof item.progress === 'number') return item.progress;

    return 0;
  }

  getProgressColor(pct: number): string {
    if (pct >= 70) return '#4ADE80';
    if (pct >= 40) return '#FFB454';
    return '#FF6B6B';
  }

  // ── Avatar ───────────────────────────────────────────────────────
  getBgColor(name: string): string {
    return getAvatarBgColor(name);
  }
  getInitials(name: string): string {
    return this.initialsService.getInitials(name);
  }

  // ── Unique Tags (parentTags + tags + technology) ────────────────
  getUniqueTags(item: any): string[] {
    const all = [
      ...(item.parentTags || []),
      ...(item.tags || []),
      ...(item.technology || []),
    ];
    return [...new Set(all)].filter((t) => !!t);
  }

  // ── Selection ────────────────────────────────────────────────────
  isSelected(item: any): boolean {
    return this.selectedIds.some((s: any) => s._id === item._id);
  }

  onRowSelect(item: any, event: Event): void {
    event.stopPropagation();
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedIds = checked
      ? this.selectedIds.some((x: any) => x._id === item._id)
        ? this.selectedIds
        : [...this.selectedIds, item]
      : this.selectedIds.filter((x: any) => x._id !== item._id);
    this.closeDropdown.emit();
    this.selectionChanged.emit(this.selectedIds);
  }

  // ── Pagination ───────────────────────────────────────────────────
  get currentPage(): number {
    return this.formData?.page ?? 1;
  }
  get totalPages(): number {
    return Math.ceil(
      (this.formData?.totalCount ?? 0) / (this.tasksLimit || 10),
    );
  }
  get pageSize(): number {
    return this.tasksLimit || 10;
  }
  get startRecord(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }
  get endRecord(): number {
    return Math.min(
      this.currentPage * this.pageSize,
      this.formData?.totalCount ?? 0,
    );
  }

  get pageNumbers(): (number | '...')[] {
    const total = this.totalPages;
    const cur = this.currentPage;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (cur > 3) pages.push('...');
    for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++)
      pages.push(i);
    if (cur < total - 2) pages.push('...');
    pages.push(total);
    return pages;
  }

  goToPage(page: number | '...') {
    if (page === '...' || page === this.currentPage) return;
    this._emitLoad(+page);
  }

  onSort(colKey: string, direction: 'asc' | 'desc') {
    if (this.formData?.records?.length > 0) {
      this.currentSort = { [colKey]: direction };
      this.sortValue = direction === 'asc' ? colKey : `-${colKey}`;
      this._emitLoad(this.currentPage);
    }
  }

  toggleSort(fieldName: string) {
    const nextDirection =
      this.currentSort[fieldName] === 'desc' ? 'asc' : 'desc';

    this.onSort(fieldName, nextDirection);
  }

  changePageSize(event: Event) {
    const size = +(event.target as HTMLSelectElement).value;
    this.tasksLimit = size;
    this._emitLoad(1, size);
  }

  private _emitLoad(page: number, limit?: number) {
    const isMyTaskContext = this.router.url.includes('/my-tasks');
    const storageKey = isMyTaskContext ? 'myTaskView' : 'tasksView';
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
    const newLimit = limit ?? this.pageSize;

    // Save pagination state
    saved.pageSize = page;
    saved.taskLimit = newLimit;
    saved.sort = this.sortValue;
    delete saved.pageNumber;
    delete saved.page;
    delete saved.limit;
    delete saved.tasksLimit;
    sessionStorage.setItem(storageKey, JSON.stringify(saved));

    const opts: any = { page, limit: newLimit, sort: this.sortValue };
    if (saved.taskSearch) opts.query = saved.taskSearch;
    if (saved.taskStatus) opts.status = saved.taskStatus;
    if (saved.taskPriority) opts.priority = saved.taskPriority;
    if (saved.riskStatus) opts.risk = saved.riskStatus;
    if (saved.domainSubdomainMappings)
      opts.domainSubdomainMappings = saved.domainSubdomainMappings;
    if (saved.assigned !== undefined) opts.assigned = saved.assigned;
    else if (saved.assignedUser !== undefined)
      opts.assigned = saved.assignedUser;
    else if (saved.taskAssigned) opts.assigned = saved.taskAssigned;

    if (saved['mappedStandards.standardName'])
      opts['mappedStandards.standardName'] =
        saved['mappedStandards.standardName'];
    else if (saved.standardName)
      opts['mappedStandards.standardName'] = saved.standardName;

    if (saved.tags) opts.tags = saved.tags;
    if (saved.parentTags) opts.parentTags = saved.parentTags;
    if (saved.technology) opts.technology = saved.technology;

    this.loadData.emit({ formId: FORM_ID.TASK_LIST, options: opts });
  }

  // ── Row menu ─────────────────────────────────────────────────────
  toggleMenu(id: string, event: Event) {
    event.stopPropagation();
    if (this.openMenuId === id) {
      this.openMenuId = null;
      this.openAboveId = null;
      return;
    }
    this.openMenuId = id;
    const target = event.currentTarget as HTMLElement;
    this.calculateDropdownPosition(id, target);

    requestAnimationFrame(() => {
      this.calculateDropdownPosition(id, target);
      this.cdr.detectChanges();
    });
  }

  calculateDropdownPosition(id: string, target?: HTMLElement) {
    if (target) {
      const rect = target.getBoundingClientRect();
      const containerRect =
        this.scrollContainer?.nativeElement.getBoundingClientRect();
      const dropdownHeight = 100;
      const gap = 12;
      const spaceBelow = containerRect
        ? containerRect.bottom - rect.bottom
        : window.innerHeight - rect.bottom;
      const spaceAbove = containerRect
        ? rect.top - containerRect.top
        : rect.top;

      if (spaceBelow < dropdownHeight + gap && spaceAbove > spaceBelow) {
        this.openAboveId = id;
      } else {
        this.openAboveId = null;
      }
      this.cdr.markForCheck();
    }
  }

  shouldOpenAbove(id: string): boolean {
    return this.openAboveId === id;
  }

  @HostListener('document:click')
  closeMenu() {
    this.openMenuId = null;
    this.openAboveId = null;
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.openMenuId) {
      this.openMenuId = null;
      this.openAboveId = null;
      this.cdr.detectChanges();
    }
  }

  onTableScroll() {
    if (this.openMenuId) {
      this.openMenuId = null;
      this.openAboveId = null;
      this.cdr.detectChanges();
    }
  }

  // ── Assign modal ─────────────────────────────────────────────────
  assignUsers(item: any, event?: Event) {
    event?.stopPropagation();
    document.dispatchEvent(new CustomEvent('closeAllOverlays'));
    this.activeModalContext = {
      nodeId: FORM_ID.TASK_ASSIGN,
      taskId: item._id,
      assigned: item.assigned,
      tasksCount: this.selectedIds.length,
    };
    document.getElementById('assign_modal')?.classList.add('show');
  }

  setModalTitle(title: string) {
    this.modalTitle = title;
  }

  closeModal() {
    document.getElementById('assign_modal')?.classList.remove('show');
    this.modalRendererRef?.resetSearchInputs();
    this.activeModalContext = null;
  }

  closeOnBackdropClick(event: MouseEvent) {
    if (event.target === document.getElementById('assign_modal'))
      this.closeModal();
  }

  onModalFormSaved(event: any): void {
    if (!event?.success) return;
    document.getElementById('assign_modal')?.classList.remove('show');
    this.activeModalContext = null;
    const isMyTaskContext = this.router.url.includes('/my-tasks');
    const storageKey = isMyTaskContext ? 'myTaskView' : 'tasksView';
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
    const opts: any = { sort: '_id' };
    if (saved.taskSearch) opts.query = saved.taskSearch;
    if (saved.taskStatus) opts.status = saved.taskStatus;
    if (saved.taskPriority) opts.priority = saved.taskPriority;
    if (saved.riskStatus) opts.risk = saved.riskStatus;
    if (saved.domainSubdomainMappings)
      opts.domainSubdomainMappings = saved.domainSubdomainMappings;
    if (saved.taskLimit) opts.limit = saved.taskLimit;
    else if (saved.limit) opts.limit = saved.limit;
    else if (saved.tasksLimit) opts.limit = saved.tasksLimit;
    if (saved.assigned !== undefined) opts.assigned = saved.assigned;
    else if (saved.assignedUser !== undefined)
      opts.assigned = saved.assignedUser;
    else if (saved.taskAssigned) opts.assigned = saved.taskAssigned;

    if (saved['mappedStandards.standardName'])
      opts['mappedStandards.standardName'] =
        saved['mappedStandards.standardName'];
    else if (saved.standardName)
      opts['mappedStandards.standardName'] = saved.standardName;

    if (saved.tags) opts.tags = saved.tags;
    if (saved.parentTags) opts.parentTags = saved.parentTags;
    if (saved.technology) opts.technology = saved.technology;

    if (saved.pageSize) opts.page = saved.pageSize;
    else if (saved.page) opts.page = saved.page;
    else if (saved.currentPage) opts.page = saved.currentPage;
    else if (saved.taskPage) opts.page = saved.taskPage;
    else if (saved.pageNumber) opts.page = saved.pageNumber;

    this.loadData.emit({ formId: FORM_ID.TASK_LIST, options: opts });
    this.graphService.triggerGraphRefresh();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.closeModal();
  }

  toggleTooltip(
    item: any,
    i: number,
    anchorEl: HTMLElement,
    event: MouseEvent,
  ) {
    if (!item) return;

    event.stopPropagation();

    const key = item._id + '-' + i;
    const wasOpen = !!this.tooltipMap[key];

    // Close all tooltips
    Object.keys(this.tooltipMap).forEach((k) => {
      this.tooltipMap[k] = false;
    });
    this.tooltipPosition = null;

    if (wasOpen) {
      return;
    }

    this.tooltipMap[key] = true;

    setTimeout(() => {
      const tooltipEl = document.getElementById('tooltip-' + key);
      if (!tooltipEl) {
        requestAnimationFrame(() => {
          const retryEl = document.getElementById('tooltip-' + key);
          if (retryEl) {
            this.positionTooltipElement(retryEl, anchorEl);
          }
        });
        return;
      }
      this.positionTooltipElement(tooltipEl, anchorEl);
    }, 20);
  }

  private positionTooltipElement(
    tooltipEl: HTMLElement,
    anchorEl: HTMLElement,
  ) {
    const rect = anchorEl.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();

    const spacing = 6;

    // Position ABOVE by default
    let top = rect.top - tooltipRect.height - spacing;

    // If it overflows the top of the screen (top < 8), open BELOW the anchor instead
    if (top < 8) {
      top = rect.bottom + spacing;
    }

    let left = rect.left + rect.width / 2 - tooltipRect.width / 2;

    // Prevent horizontal overflow
    if (left < 8) left = 8;
    if (left + tooltipRect.width > window.innerWidth) {
      left = window.innerWidth - tooltipRect.width - 8;
    }

    this.tooltipPosition = { top, left };
  }

  handleScroll = (event: Event) => {
    if (this.openMenuId) {
      this.openMenuId = null;
      this.openAboveId = null;
      this.cdr.detectChanges();
    }

    const isAnyTooltipOpen = Object.values(this.tooltipMap).some(
      (val) => val === true,
    );
    if (!isAnyTooltipOpen) return;

    const target = event.target as HTMLElement;
    if (
      target &&
      typeof target.closest === 'function' &&
      target.closest('.cv-tooltip-container')
    ) {
      return;
    }

    // Close all tooltips
    Object.keys(this.tooltipMap).forEach((k) => {
      this.tooltipMap[k] = false;
    });
    this.tooltipPosition = null;
  };

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const isInsideTooltip = target.closest('[id^="tooltip-"]');
    const isBadge =
      target.closest('.cv-sub-count') || target.closest('.bg-danger');
    const isTag = target.closest('.cv-tag');
    if (!isInsideTooltip && !isBadge && !isTag) {
      Object.keys(this.tooltipMap).forEach((k) => (this.tooltipMap[k] = false));
      this.tooltipPosition = null;
    }
  }
}
